import axios from "axios"
import axiosRetry, { isNetworkOrIdempotentRequestError } from 'axios-retry'
import catchUnauthorizedError from '@/utils/catchUnauthorizedError'
import catchForbiddenError from '@/utils/catchForbiddenError'
import catch404 from '@/utils/catch404'
import eventBus from "@/eventBus"
import router from '@/router'
import { getRecordingURL } from '@/utils/logrocketInit'
import { apiErrorHandler } from "@/utils/apiErrorHandler"
import { logInfra, sanitizeUrl } from '@/utils/logInfra'
import mergeWith from 'lodash/mergeWith'
import { ERROR_MESSAGES } from "@/constants/ERROR_MESSAGES"
import { EVENT_BUS_EVENTS } from "@/constants/EVENT_BUS_EVENTS"
import { useToast } from 'vue-toastification'
import { h } from 'vue'

function simpleNetworkErrorCheck(error) {
  if (!error?.code) {
    return true
  } else {
    return isNetworkOrIdempotentRequestError(error)
  }
}

const httpClient = axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL + '/v1',
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})


axiosRetry(httpClient, {
  retries: 1,
  retryCondition: simpleNetworkErrorCheck,
  retryDelay: () => 2000,
  onRetry: (retryCount, error, requestConfig) => {
    logInfra('[infra:retry-attempt]', buildInfraPayload(error, requestConfig, { attempt: retryCount }))
    // axios-retry swallows the first failure before the response interceptor sees it.
    // Emit the classified cause here so the timeline shows WHY the first attempt
    // failed (otherwise only [infra:retry-attempt] appears, with no category).
    // capture+track suppressed: this is a transient first-attempt blip. The real
    // captureMessage/track for network-error will fire from the response interceptor
    // if the final attempt also fails, so we avoid double-counting analytics events.
    logInfra(
      classifyInfraCause(error),
      buildInfraPayload(error, requestConfig, { attempt: retryCount - 1 }),
      { capture: false, track: false },
    )
  },
})

let isUnloading = false
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => { isUnloading = true })
  window.addEventListener('pagehide', () => { isUnloading = true })
  // bfcache restore reactivates the page — must reset the flag, otherwise
  // every subsequent request is silently dropped from infra logging.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) isUnloading = false
  })
}

function buildInfraPayload(error, config, overrides = {}) {
  const signal = config?.signal
  let signalState = 'no-signal'
  if (signal) signalState = signal.aborted ? 'aborted' : 'open'

  return {
    url: sanitizeUrl(config?.url),
    method: String(config?.method ?? '').toUpperCase(),
    code: error?.code ?? 'no-code',
    name: error?.name ?? 'no-name',
    attempt: config?.metadata?.attempt ?? 0,
    hadSignal: Boolean(signal),
    signalState,
    cancelSource: config?.metadata?.cancelSource ?? 'unknown',
    isUnloading,
    ...overrides,
  }
}

// Mirrors the inline classification branches in the response interceptor.
// They are intentionally not refactored to share this helper because each
// interceptor branch does an early `return` that controls flow; collapsing
// them would require a larger restructure. Keep both in sync on any change.
function classifyInfraCause(error) {
  if (axios.isCancel(error)) return '[infra:client-cancel]'
  if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') return '[infra:client-timeout]'
  if (error?.code === 'ERR_NETWORK') return '[infra:network-error]'
  return '[infra:unknown-error]'
}

httpClient.interceptors.request.use((config) => {
  // axios-retry registers its own request interceptor AFTER this one. Axios runs
  // interceptors in REVERSE registration order, so on attempt 0, config['axios-retry']
  // is undefined here — the ?? 0 fallback handles it. On retry, axios-retry has
  // already populated retryCount by the time this runs.
  config.metadata = {
    ...(config.metadata || {}),
    attempt: config['axios-retry']?.retryCount ?? 0,
  }

  const token = localStorage.getItem('token')

  if (config.url.includes('{currentCompanySlug}')) {
    const companySlug = router.currentRoute.value.params.company || sessionStorage.getItem('currentCompanySlug')

    if (!companySlug) {
      throw new Error(ERROR_MESSAGES.missedCurrentCompany)
    }

    config.url = config.url.replace('{currentCompanySlug}', companySlug)
  }

  if (token && !config.ignoreGlobalAuthTokenSet) {
    config.headers.Authorization = `Bearer ${token}`
  }

  const sessionURL = getRecordingURL()
  if (sessionURL) {
    config.headers['X-LogRocket-URL'] = sessionURL
  }

  return config
}, (err) => Promise.reject(err))

httpClient.interceptors.response.use((response) => {
  const attempt = response.config?.metadata?.attempt ?? 0
  if (attempt > 0) {
    logInfra('[infra:retry-success]', buildInfraPayload({ code: 'success', name: 'success' }, response.config))
  }
  return response
}, (error) => {
  if (error.response?.data?.type === 'fileValidationError') {
    const toast = useToast()
    toast.error(() => h('div', [
      error.response?.data?.error,
      h('br'),
      `File: ${error.response?.data?.file}`,
    ]))
    return Promise.reject(error)
  }

  if (!axios.isAxiosError(error) || error.config?.skipResponseInterceptor) {
    return Promise.reject(error)
  }

  if (axios.isCancel(error)) {
    logInfra('[infra:client-cancel]', buildInfraPayload(error, error.config))
    return Promise.reject(error)
  }

  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    logInfra('[infra:client-timeout]', buildInfraPayload(error, error.config))
    return Promise.reject(error)
  }

  if (error.response?.data?.errors?.relatedFeatures?.length) {
    return Promise.reject(error)
  }

  // Retry-exhausted emission is category-independent: whether the failure is
  // ERR_NETWORK, 5xx, ECONNRESET, or any other axios-retryable code, if we ran
  // out of retries the timeline must show it. This is the single emission point.
  const retryState = error.config?.['axios-retry']
  const isExhausted = Boolean(retryState && retryState.retryCount > 0 && retryState.retryCount >= retryState.retries)
  if (isExhausted) {
    logInfra('[infra:retry-exhausted]', buildInfraPayload(error, error.config))
  }

  if (error.code === 'ERR_NETWORK' && !isUnloading) {
    const attempt = error.config?.metadata?.attempt ?? 0
    // captureMessage suppressed when attempt === 0 to keep Issues clean from
    // transient single-blip network errors. log + track still fire.
    logInfra(
      '[infra:network-error]',
      buildInfraPayload(error, error.config),
      { capture: attempt > 0 },
    )
  }

  if (error?.response?.status === 401 && !error?.config?.skipDefaultUnauthorizedBehaviour) {
    catchUnauthorizedError()
    return Promise.reject(error)
  }
  if (error?.response?.status === 403 && !error?.config?.skipDefaultForbiddenBehaviour) {
    if (error?.response?.data?.errors?.automationValidation) {
      const { automationValidation: automationValidations } = error.response.data.errors

      const customFieldsErrorsMap = new Map()

      automationValidations.forEach((automationValidation) => {
        automationValidation.customFields.forEach((customFieldSlug) => {
          const storedValue = customFieldsErrorsMap.get(customFieldSlug)
          if (storedValue) {
            return customFieldsErrorsMap.set(
              customFieldSlug,
              [storedValue, automationValidation.message].join('<br>'),
            )
          }

          customFieldsErrorsMap.set(
            customFieldSlug,
            automationValidation.message,
          )
        })

        Object.entries(automationValidation.childObjects).forEach(([customFieldSlug, value]) => {
          const storedValue = customFieldsErrorsMap.get(customFieldSlug)

          const formattedValueWithMessage = Object.entries(value).reduce((acc, [key, fieldValue]) => {
            return {
              ...acc,
              [key]: Object.fromEntries(fieldValue.map((val) => [val, automationValidation.message])),
            }
          }, {})

          if (storedValue) {
            return customFieldsErrorsMap.set(
              customFieldSlug,
              mergeWith(storedValue, formattedValueWithMessage, (objValue, srcValue) => {
                if (typeof objValue === 'string') {
                  return [objValue, srcValue].join('<br>')
                }
              }),
            )
          }

          customFieldsErrorsMap.set(
            customFieldSlug,
            formattedValueWithMessage,
          )
        })
      })

      if (!error?.config?.ignoreEventBus) {
        eventBus.$emit(EVENT_BUS_EVENTS.STATUS_ERROR_CAPTURED, {
          automationValidations,
          customFieldsErrorsMap,
          hideValidationSections: Boolean(error?.config?.hideValidationSections),
          recordId: error?.config?.validationRecordId || null,
        })
      }

      return Promise.reject({
        automationValidations,
        customFieldsErrorsMap,
        hideValidationSections: Boolean(error?.config?.hideValidationSections),
        recordId: error?.config?.validationRecordId || null,
      })
    }

    catchForbiddenError()
    return Promise.reject(error)
  }
  if (error?.response?.status === 404 && !error.config.ignoreGlobalNotFoundCatch) {
    catch404()
    return Promise.reject(error)
  }

  if (error?.response?.status === 404 && error.config.ignoreGlobalNotFoundCatch) {
    return Promise.reject(error)
  }

  if (error?.response?.status === 413) {
    return Promise.reject(error)
  }

  if (!error?.response?.status || String(error?.response?.status || '')[0] === '5') {
    if (error?.config['axios-retry'].retryCount >= 1) {
      eventBus.$emit(EVENT_BUS_EVENTS.REQUEST_ERROR_CAPTURED, 504)
      return Promise.reject(error)
    }
  }

  // True catch-all for axios errors not classified by any specific branch above.
  // ERR_NETWORK is excluded because the dedicated network-error block already logged it.
  if (error.code !== 'ERR_NETWORK') {
    logInfra('[infra:unknown-error]', buildInfraPayload(error, error.config))
  }

  apiErrorHandler(error)

  return Promise.reject(error)
})

export default httpClient
